import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  loadAnalyticsSettings,
  startSession,
  updateSessionExit,
  recordPageView,
  getOrCreateSessionId,
} from '../lib/analytics';
import { supabase } from '../lib/supabase';

let cachedTrackingEnabled: boolean | null = null;
let cachedRealtimeEnabled: boolean | null = null;

const SESSION_INIT_KEY = 'analytics_session_initialized';

export function useAnalyticsTracker() {
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const pageEntryRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>('');
  const trackingEnabledRef = useRef<boolean>(false);
  const initDoneRef = useRef<boolean>(false);
  const pendingPathRef = useRef<string | null>(null);
  const pendingEntryRef = useRef<number>(Date.now());
  const presenceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    async function init() {
      let trackingEnabled = cachedTrackingEnabled;
      let realtimeEnabled = cachedRealtimeEnabled;

      if (trackingEnabled === null) {
        const settings = await loadAnalyticsSettings();
        if (settings !== null) {
          trackingEnabled = settings.tracking_enabled;
          realtimeEnabled = settings.realtime_enabled;
          cachedTrackingEnabled = trackingEnabled;
          cachedRealtimeEnabled = realtimeEnabled;
        } else {
          trackingEnabled = true;
          realtimeEnabled = true;
        }
      }

      trackingEnabledRef.current = trackingEnabled;

      if (!trackingEnabled) {
        initDoneRef.current = true;
        return;
      }

      const sessionId = getOrCreateSessionId();
      sessionIdRef.current = sessionId;

      const alreadyInitialized = sessionStorage.getItem(SESSION_INIT_KEY) === sessionId;
      if (!alreadyInitialized) {
        sessionStorage.setItem(SESSION_INIT_KEY, sessionId);
        await startSession(location.pathname);
      }

      initDoneRef.current = true;

      const entryPath = pendingPathRef.current ?? location.pathname;
      const entryTime = pendingPathRef.current ? pendingEntryRef.current : Date.now();
      pendingPathRef.current = null;

      if (!currentPathRef.current) {
        currentPathRef.current = entryPath;
        pageEntryRef.current = entryTime;
        recordPageView(sessionId, entryPath);
      }

      if (realtimeEnabled !== false && !presenceChannelRef.current) {
        const presenceChannel = supabase.channel('visitor-presence', {
          config: { presence: { key: sessionId } },
        });
        presenceChannelRef.current = presenceChannel;
        presenceChannel.subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            await presenceChannel.track({
              path: location.pathname,
              ts: Date.now(),
            });
          }
        });
      }
    }
    init();

    return () => {
      if (presenceChannelRef.current) {
        supabase.removeChannel(presenceChannelRef.current);
        presenceChannelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!initDoneRef.current) {
      pendingPathRef.current = location.pathname;
      pendingEntryRef.current = Date.now();
      return;
    }
    if (!trackingEnabledRef.current) return;
    if (currentPathRef.current === location.pathname) return;

    const now = Date.now();
    const previousPath = currentPathRef.current;

    if (previousPath) {
      updateSessionExit(sessionIdRef.current ?? getOrCreateSessionId(), location.pathname);
    }

    currentPathRef.current = location.pathname;
    pageEntryRef.current = now;

    recordPageView(sessionIdRef.current, location.pathname);

    if (presenceChannelRef.current) {
      presenceChannelRef.current.track({
        path: location.pathname,
        ts: Date.now(),
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleUnload = () => {
      if (!trackingEnabledRef.current || !currentPathRef.current) return;
      updateSessionExit(sessionIdRef.current ?? getOrCreateSessionId(), currentPathRef.current);
      currentPathRef.current = '';
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);
}
